import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {filter, map, takeUntil} from 'rxjs/operators';

import {PayTypeContextChange, PayTypeSelector, PayTypeWithCliRegIds} from '../../../+payrolls/payrolls.model';
import {PayrollUtilsService} from '../../../+payrolls/utils/payroll-utils.service';
import {Destroyable} from '../../../destroyable.component';
import {PopulationFilterSelector} from '../../../navigation/navbar/context-bar/context-bar.interfaces';
import {ApiService} from '../../api';
import {CriterionDescriptor} from '../../population/create-population/create-population.interfaces';
import {ContractIndexCriteriaDTO} from '../../api/GaApi';

@Injectable({
  providedIn: 'root'
})
export class EmployeesContextUtilsService extends Destroyable {

  private static CONTEXT_PAY_TYPE: string = 'context_pay_type';

  public contextPayType$: BehaviorSubject<PayTypeSelector> = new BehaviorSubject<PayTypeSelector>(null);
  public contextPopulation$: BehaviorSubject<PopulationFilterSelector> = new BehaviorSubject<PopulationFilterSelector>(null);
  public resetSelectedFilters$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private payrollUtilsService: PayrollUtilsService,
              private api: ApiService) {
    super();

    this.contextPayType$
      .pipe(
        takeUntil(this.componentDestroyed$),
        filter(payType => payType != null)
      )
      .subscribe((newPayType: PayTypeSelector) => {
        this.updateLocalStoragePayType(newPayType);
      });
  }

  public getContextPayType(): PayTypeContextChange {
    const selectedPayType = this.contextPayType$.getValue();
    const storedContextPayType = this.getStoredContextPayType();
    if (selectedPayType && this.payrollUtilsService.isPayType(selectedPayType)) {
      return {
        name: selectedPayType.name,
        transId: selectedPayType.transId,
        cliRegIds: selectedPayType.cliRegIds,
        disablePayTypeFilter: true
      };
    } else if (storedContextPayType) {
      return {
        name: storedContextPayType.name,
        transId: storedContextPayType.transId,
        cliRegIds: storedContextPayType.cliRegIds,
        disablePayTypeFilter: true
      };
    }
    return {
      name: '',
      transId: '',
      cliRegIds: [],
      disablePayTypeFilter: false
    };
  }

  public getStoredContextPayType(): { name: string, transId: string, cliRegIds: number[] } {
    return JSON.parse(localStorage.getItem(EmployeesContextUtilsService.CONTEXT_PAY_TYPE));
  }

  public listenContextPayType(): Observable<PayTypeContextChange> {
    return this.contextPayType$
      .pipe(
        filter(payType => payType != null),
        map((newPayType: PayTypeSelector) => {
          if (this.payrollUtilsService.isPayType(newPayType)) {
            newPayType = newPayType as PayTypeWithCliRegIds;
            return {
              name: newPayType.name,
              transId: newPayType.transId,
              cliRegIds: newPayType.cliRegIds,
              disablePayTypeFilter: true
            };
          } else {
            return {
              name: '',
              transId: '',
              cliRegIds: [],
              disablePayTypeFilter: false
            };
          }
        })
      );
  }

  public updateLocalStoragePayType(newPayType: PayTypeSelector) {
    if (this.payrollUtilsService.isPayType(newPayType)) {
      newPayType = newPayType as PayTypeWithCliRegIds;
      localStorage.setItem(
        EmployeesContextUtilsService.CONTEXT_PAY_TYPE,
        JSON.stringify({name: newPayType.name, transId: newPayType.transId, cliRegIds: newPayType.cliRegIds})
      );
    } else {
      localStorage.removeItem(EmployeesContextUtilsService.CONTEXT_PAY_TYPE);
    }
  }

  public getContextPopulation(): Observable<PopulationFilterSelector> {
    return this.contextPopulation$.asObservable();
  }

  public updateContextPopulation(population: PopulationFilterSelector) {
    this.contextPopulation$.next(population);
  }

  public getContractIndexCriteria(): Observable<ContractIndexCriteriaDTO[]> {
    return this.api.get('/ga/api/v2/contract-index-criteria');
  }

  public getCriteriaDescriptors(forAllowedCliRegsOnly = true): Observable<CriterionDescriptor[]> {
    return this.api.get(`/ga/api/v2/contract-index-criteria/zone-descriptor?forAllowedCliRegsOnly=${forAllowedCliRegsOnly}`);
  }

  public refreshPopulation(activeRoute: string) {
    const activeRouteArray = activeRoute.slice(1).split('/');
    let redirectionUrlArray;
    switch (activeRouteArray[0]) {
      case 'employees':
      case 'payroll' :
      case 'settings':
        if (activeRouteArray.length > 2) {
          redirectionUrlArray = window.location.href.split('/');
          const redirectionUrl = redirectionUrlArray.slice(0, redirectionUrlArray.length - 1).join('/');
          window.location.href = redirectionUrl;
          window.location.reload();
        } else {
          const redirectionUrl = window.location.href.split('?')[0];
          window.location.href = redirectionUrl;
          window.location.reload();
        }
        break;
      default:
        window.location.reload();
    }
  }

}
